import { json, LoaderFunctionArgs } from '@remix-run/node';
import { MetaFunction, useLoaderData } from '@remix-run/react';
import React from 'react';
import { getPageBySlug } from '~/providers/pages/pages';

// Loader function which we define for fetch the page by using the slug
export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const page = await getPageBySlug(params.slug!, request);
  return json({ page });
};

// Meta function defined for the SEO
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const page = data?.page;

  if (!page) {
    return [
      { title: 'Page Not Found' },
      {
        name: 'description',
        content: 'The requested page could not be found.',
      },
    ];
  }

  const metaTags = [
    { title: page.metaTitle || page.title },
    { name: 'description', content: page.metaDescription || '' },
    { name: 'keywords', content: page.metaKeywords || '' },
    { property: 'og:title', content: page.metaTitle || page.title },
    { property: 'og:description', content: page.metaDescription || '' },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: page.metaTitle || page.title },
    { name: 'twitter:description', content: page.metaDescription || '' },
  ];

  return metaTags.filter((tag) => {
    if ('content' in tag && !tag.content) return false;
    if ('property' in tag && tag.property?.startsWith('og:') && !tag.content)
      return false;
    return true;
  });
};

// Component to render HTML content dynamically with stable keys
function ContentRenderer({ content }: { content: string }) {
  if (!content) return null;

  const parseHtmlContent = (htmlString: string) => {
    if (typeof window === 'undefined') return [];

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    return Array.from(tempDiv.children).map((element, i) => {
      const tag = element.tagName.toLowerCase();
      const html = element.innerHTML;
      const text = element.textContent || '';
      const baseKey = `${tag}-${i}`;

      switch (tag) {
        case 'h2':
          return (
            <h2
              key={baseKey}
              className="text-3xl font-bold text-primary mb-6 mt-8 first:mt-0"
            >
              {text}
            </h2>
          );
        case 'h3':
          return (
            <h3
              key={baseKey}
              className="text-xl font-semibold text-primary/90 mb-4 mt-6 border-b-2 border-primary/20 pb-2"
            >
              {text}
            </h3>
          );
        case 'p':
          if (html.includes('📧') || html.includes('📍')) {
            return (
              <div
                key={`info-${baseKey}`}
                className="bg-primary/5 p-6 rounded-lg border border-primary/20 mt-4"
              >
                <div
                  className="text-gray-700 leading-relaxed [&>strong]:font-semibold [&>strong]:text-primary/90 [&>br]:block [&>br]:mb-2"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </div>
            );
          } else {
            return (
              <p
                key={baseKey}
                className="text-gray-700 leading-relaxed mb-4 [&>strong]:font-semibold [&>strong]:text-primary"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          }
        case 'ul':
          const items = Array.from(element.querySelectorAll('li')).map(
            (li, j) => (
              <li key={`li-${i}-${j}`} className="flex items-start mb-3">
                <div className="w-2 h-2 bg-primary/50 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                <div
                  className="text-gray-700 leading-relaxed [&>strong]:font-semibold [&>strong]:text-primary [&>p]:inline"
                  dangerouslySetInnerHTML={{ __html: li.innerHTML }}
                />
              </li>
            ),
          );
          return (
            <ul key={`ul-${baseKey}`} className="ml-4 mb-6">
              {items}
            </ul>
          );
        default:
          return (
            <div
              key={`div-${baseKey}`}
              className="text-gray-700 leading-relaxed mb-4"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          );
      }
    });
  };

  const [parsedContent, setParsedContent] = React.useState<JSX.Element[]>([]);

  React.useEffect(() => {
    setParsedContent(parseHtmlContent(content));
  }, [content]);

  return <div className="space-y-4">{parsedContent}</div>;
}

// Main About Page component
export default function AboutPage() {
  const { page } = useLoaderData<typeof loader>();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Your Store Name',
    description: page?.metaDescription || '',
    url: typeof window !== 'undefined' ? window.location.origin : '',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <section className="max-w-4xl mx-auto py-12 px-4 sm:px-6 md:px-6 lg:px-8 xl:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="sr-only">{page?.title || 'About Us'}</h1>
          <ContentRenderer content={page?.content || ''} />
        </div>
      </section>
    </>
  );
}
